import { Button, Checkbox, Label, Select, Spinner, Textarea } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserRound } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormError, FormField, PhotoField } from '@/components';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type Customer, useCreateCustomer, useCustomer, useUpdateCustomer } from '../api';

const customerFormSchema = z.object({
  name: z.string().min(2, 'Informe o nome').max(120),
  image: z.string().max(400_000),
  email: z.union([z.string().email('E-mail invalido'), z.literal('')]),
  phone: z.string().max(40),
  document: z.string().max(20),
  notes: z.string().max(1000),
  unitId: z.string(),
  isFavorite: z.boolean(),
  isActive: z.boolean(),
});

export type CustomerFormValues = z.infer<typeof customerFormSchema>;

interface CustomerFormProps {
  /** Sem id, e um cadastro novo. */
  id?: string;
  /** Preenche o cadastro novo com o que ja se sabe (ex.: o WhatsApp digitado). */
  defaults?: Partial<CustomerFormValues>;
  /** Id do formulario, para o botao de salvar viver fora dele (rodape de modal). */
  formId?: string;
  /** Esconde o proprio botao de salvar - quem submete e o rodape. */
  hideSubmit?: boolean;
  onSaved: (customer: Customer) => void;
}

/**
 * Cadastro do cliente. Mora aqui, e nao na pagina, porque a mesma ficha e
 * usada em dois lugares: a tela de cadastro e a modal que abre por cima do
 * agendamento, quando falta completar os dados de quem esta sendo atendido.
 */
export function CustomerForm({ id, defaults, formId, hideSubmit, onSaved }: CustomerFormProps) {
  const isEdit = Boolean(id);
  const { user } = useSession();
  const units = user?.units ?? [];

  const existing = useCustomer(id);
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    values: existing.data
      ? {
          name: existing.data.name,
          image: existing.data.image ?? '',
          email: existing.data.email ?? '',
          phone: existing.data.phone ?? '',
          document: existing.data.document ?? '',
          notes: existing.data.notes ?? '',
          unitId: existing.data.unitId ?? '',
          isFavorite: existing.data.isFavorite,
          isActive: existing.data.isActive,
        }
      : undefined,
    defaultValues: {
      name: '',
      image: '',
      email: '',
      phone: '',
      document: '',
      notes: '',
      // Com uma unidade so, ela ja vem escolhida (o campo nem aparece).
      unitId: units.length === 1 ? (units[0]?.id ?? '') : '',
      isFavorite: false,
      isActive: true,
      ...defaults,
    },
  });

  const mutationError = createCustomer.error ?? updateCustomer.error;
  const isSaving = createCustomer.isPending || updateCustomer.isPending;
  const photo = watch('image');

  if (isEdit && existing.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (isEdit && existing.isError) {
    return <p className="text-sm text-destructive">{toErrorMessage(existing.error)}</p>;
  }

  function onSubmit(values: CustomerFormValues) {
    const input = { ...values, unitId: values.unitId || undefined };

    if (isEdit && id) {
      updateCustomer.mutate({ id, input }, { onSuccess: onSaved });
      return;
    }

    createCustomer.mutate(input, { onSuccess: onSaved });
  }

  return (
    <form id={formId} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormError message={mutationError ? toErrorMessage(mutationError) : null} />

      {/* A foto sai do aparelho: pedir uma URL publica obrigava a hospedar a
          imagem em outro lugar antes de cadastrar o cliente. */}
      <PhotoField
        label="Foto"
        value={photo}
        onChange={(valor) => setValue('image', valor, { shouldDirty: true })}
        fallback={<UserRound className="h-6 w-6" />}
      />

      <FormField label="Nome" error={errors.name?.message} {...register('name')} />

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="E-mail"
          type="email"
          error={errors.email?.message}
          {...register('email')}
        />
        {/* type/inputMode certos: no celular abre o teclado numerico. */}
        <FormField
          label="Telefone"
          type="tel"
          error={errors.phone?.message}
          {...register('phone')}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Documento"
          inputMode="numeric"
          error={errors.document?.message}
          {...register('document')}
        />

        {units.length > 1 ? (
          <div className="space-y-2">
            <Label htmlFor="unitId">Unidade</Label>
            <Select id="unitId" {...register('unitId')}>
              <option value="">Sem unidade preferencial</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Observacoes</Label>
        <Textarea id="notes" {...register('notes')} />
      </div>

      <div className="flex flex-wrap gap-4">
        <Checkbox label="Favorito" {...register('isFavorite')} />

        <Checkbox label="Cliente ativo" {...register('isActive')} />
      </div>

      {hideSubmit ? null : (
        <Button type="submit" disabled={isSaving}>
          {isSaving ? <Spinner /> : null}
          {isEdit ? 'Salvar alteracoes' : 'Criar cliente'}
        </Button>
      )}
    </form>
  );
}
